import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { AlertTriangle } from 'lucide-react';

/**
 * SessionTimeoutWarning
 *
 * Shows a modal with a countdown when the user's refresh token is close to
 * expiring, giving them a one-click way to keep the session alive.
 *
 * Design notes
 * ────────────
 * • Because of the sliding refresh-token rotation on the backend, any
 *   authenticated API request resets the 30-day clock. An active user will
 *   therefore almost never see this modal — it only fires when the user has
 *   been genuinely inactive for close to the full refresh window.
 * • Clicking "Stay logged in" calls the onExtend callback (which should hit
 *   /auth/refresh). Success rotates the token and reschedules the next
 *   warning. If the refresh fails, the component surfaces the failure and
 *   waits for the parent to force a logout.
 * • If the countdown hits zero with no action, the component calls onLogout.
 *
 * Props
 *   open        : boolean — controls modal visibility
 *   expiresAt   : Date    — when the refresh token becomes invalid
 *   onExtend    : async () => void  — must resolve when the session is
 *                                     successfully extended
 *   onLogout    : ()  => void       — called if the timer runs out
 *   onOpenChange: (b) => void       — for dialog close-button / ESC
 */
const SessionTimeoutWarning = ({
  open,
  expiresAt,
  onExtend,
  onLogout,
  onOpenChange,
}) => {
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [extending, setExtending] = useState(false);

  useEffect(() => {
    if (!open || !expiresAt) return undefined;
    const tick = () => {
      const ms = new Date(expiresAt).getTime() - Date.now();
      setSecondsLeft(Math.max(0, Math.floor(ms / 1000)));
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [open, expiresAt]);

  // Auto-logout when the counter reaches zero.
  useEffect(() => {
    if (open && secondsLeft === 0 && expiresAt) {
      onLogout?.();
    }
  }, [open, secondsLeft, expiresAt, onLogout]);

  const handleStay = async () => {
    if (extending) return;
    setExtending(true);
    try {
      await onExtend?.();
      // Parent closes the dialog after a successful extend.
    } finally {
      setExtending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-600">
            <AlertTriangle className="h-5 w-5" />
            Session about to expire
          </DialogTitle>
          <DialogDescription>
            Your session will expire in{' '}
            <span className="font-mono font-semibold text-foreground">
              {formatCountdown(secondsLeft)}
            </span>
            . Click "Stay logged in" to keep working, otherwise you'll be
            logged out automatically.
          </DialogDescription>
        </DialogHeader>

        <DialogFooter className="gap-2 flex-col sm:flex-row">
          <Button
            variant="outline"
            onClick={() => onLogout?.()}
            disabled={extending}
          >
            Log out now
          </Button>
          <Button onClick={handleStay} disabled={extending || secondsLeft === 0}>
            {extending ? 'Extending…' : 'Stay logged in'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

function formatCountdown(total) {
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

export default SessionTimeoutWarning;
